import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { HadPrincipal, PrincipalAuthentication } from "./HadPrincipal";
import { NoPrincipal } from "./NoPrincipal";
import Axios from "axios";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { EnterpriseAuthentication } from "./SubmitInfo/enterprise-authentication";
import { PersonalAuthentication } from "./SubmitInfo/personal-authentication";
import { ConfigCard } from "@/components/ConfigCard";
import { AuthingModal } from "@/components/AuthingModal";
import { useMediaSize } from "@/hooks/useMediaSize";

/**
 * 个人认证弹框
 */
const PersonalAuthenticationModal: FC<{ isModalVisible: boolean; handleOkCb: Function; handleCancelCb: Function }> = ({
  isModalVisible,
  handleOkCb,
  handleCancelCb,
}) => {
  const personalAuthenticationForm = useRef<any>();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const handleOk = () => {
    personalAuthenticationForm.current.validateFields().then((res: any) => {
      const user: any = window.__user__;
      const submitInfo = {
        type: "P",
        name: res.name,
        idCard: res.idNumber,
        ext: res.bankCard,
      };
      setConfirmLoading(true);
      Axios.post(`/api/v2/users/${user.id}/principal_authentication`, submitInfo)
        .then((res: any) => {
          if (res.code !== 200) {
            message.error(res.message);
            setConfirmLoading(false);
          } else {
            message.success(t("user.authenticationSuccess"));
            setConfirmLoading(false);
            handleOkCb();
          }
        })
        .catch(() => {
          setConfirmLoading(false);
        });
    });
  };
  const handleCancel = () => {
    handleCancelCb();
  };
  const { t } = useTranslation();

  return (
    <>
      <AuthingModal
        title={t("user.personalAuthentication")}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        okText={t("common.submit")}
        cancelText={t("common.cancel")}
      >
        <PersonalAuthentication ref={personalAuthenticationForm} />
      </AuthingModal>
    </>
  );
};

/**
 * 企业认证弹框
 */
const EnterpriseAuthenticationModal: FC<{
  isModalVisible: boolean;
  handleOkCb: Function;
  handleCancelCb: Function;
}> = ({ isModalVisible, handleOkCb, handleCancelCb }) => {
  const enterpriseAuthenticationForm = useRef<any>();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const handleOk = () => {
    enterpriseAuthenticationForm.current.validateFields().then((res: any) => {
      const user: any = window.__user__;
      const submitInfo = {
        type: "E",
        name: res.enterpriseName,
        idCard: res.enterpriseCode,
        ext: res.legalPersonName,
      };
      setConfirmLoading(true);
      Axios.post(`/api/v2/users/${user.id}/principal_authentication`, submitInfo)
        .then((res: any) => {
          if (res.code !== 200) {
            message.error(res.message);
            setConfirmLoading(false);
          } else {
            message.success(t("user.authenticationSuccess"));
            setConfirmLoading(false);
            handleOkCb();
          }
        })
        .catch(() => {
          setConfirmLoading(false);
        });
    });
  };
  const handleCancel = () => {
    handleCancelCb();
  };
  const { t } = useTranslation();

  return (
    <>
      <AuthingModal
        title={t("user.enterpriseAuthentication")}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        okText={t("common.submit")}
        cancelText={t("common.cancel")}
      >
        <EnterpriseAuthentication ref={enterpriseAuthenticationForm} />
      </AuthingModal>
    </>
  );
};

/**
 * 用户主体认证
 */
export const UserAuthentication: FC = () => {
  const user = window.__user__;
  const [principalAuthentication, setPrincipalAuthentication] = useState({} as PrincipalAuthentication);
  const [isSpinning, setIsSpinning] = useState(false);
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [needAuthenticationType, setNeedAuthenticationType] = useState("P");

  const { isPhoneMedia } = useMediaSize();
  const loadAuthenticationInfo = useCallback(async () => {
    setIsSpinning(true);
    try {
      if (user && user.id) {
        const result: any = await Axios.get(`/api/v2/users/${user.id}/principal_authentication`);
        if (result && result.code === 200) {
          setPrincipalAuthentication(result.data);
        } else {
          setPrincipalAuthentication({} as PrincipalAuthentication);
        }
      }
      setIsSpinning(false);
    } catch (e) {
      setIsSpinning(false);
    }
  }, [user]);

  const delAuthentication = useCallback(async () => {
    setIsSpinning(true);
    try {
      if (user && user.id) {
        const result: any = await Axios.delete(`/api/v2/users/me/principal_authentication`);
        if (result && result.code === 200) {
          message.success("解绑成功");
          await loadAuthenticationInfo();
        } else {
          message.error("解绑失败");
        }
      }
      setIsSpinning(false);
    } catch (e) {
      setIsSpinning(false);
    }
  }, [user, loadAuthenticationInfo]);



  useEffect(() => {
    (async () => {
      await loadAuthenticationInfo();
    })();
  }, [loadAuthenticationInfo]);

  const toEnterpriseAuthentication = () => {
    setNeedAuthenticationType("E");
    setIsModalVisible(true);
  };
  const toPersonalAuthentication = () => {
    setNeedAuthenticationType("P");
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    setIsModalVisible(false);
    await loadAuthenticationInfo();
  };

  const unBindPersonalAuthentication = async () => {
    await delAuthentication();
  }
  const unBindEnterpriseAuthentication = async () => {
    await delAuthentication();
  }

  return isPhoneMedia && principalAuthentication && principalAuthentication.principalType ? (
    <HadPrincipal
      userPoolId={principalAuthentication.userPoolId}
      userId={principalAuthentication.userId}
      principalType={principalAuthentication.principalType}
      principalName={principalAuthentication.principalName}
      principalCode={principalAuthentication.principalCode}
      authenticationTime={principalAuthentication.authenticationTime}
      toEnterpriseAuthentication={toEnterpriseAuthentication}
      unBindPersonalAuthentication={unBindPersonalAuthentication}
      unBindEnterpriseAuthentication={unBindEnterpriseAuthentication}
    />
  ) : (
    <ConfigCard
      title={t("user.choosePrincipalAuthenticationType")}
      description={t("user.choosePrincipalAuthenticationDescription")}
      extra={null}
      loading={isSpinning}
    >
      {principalAuthentication && principalAuthentication.principalType ? (
        <HadPrincipal
          userPoolId={principalAuthentication.userPoolId}
          userId={principalAuthentication.userId}
          principalType={principalAuthentication.principalType}
          principalName={principalAuthentication.principalName}
          principalCode={principalAuthentication.principalCode}
          authenticationTime={principalAuthentication.authenticationTime}
          toEnterpriseAuthentication={toEnterpriseAuthentication}
          unBindPersonalAuthentication={unBindPersonalAuthentication}
          unBindEnterpriseAuthentication={unBindEnterpriseAuthentication}
        />
      ) : (
        <NoPrincipal
          toPersonalAuthentication={toPersonalAuthentication}
          toEnterpriseAuthentication={toEnterpriseAuthentication}
        />
      )}
      {needAuthenticationType === "P" ? (
        <PersonalAuthenticationModal
          isModalVisible={isModalVisible}
          handleOkCb={() => handleModalOk()}
          handleCancelCb={() => setIsModalVisible(false)}
        />
      ) : (
        <EnterpriseAuthenticationModal
          isModalVisible={isModalVisible}
          handleOkCb={() => handleModalOk()}
          handleCancelCb={() => setIsModalVisible(false)}
        />
      )}
    </ConfigCard>
  );
};
